import { Box, Icon, IconProps, SpaceBetween } from '@amzn/awsui-components-console';
import { colorTextStatusInfo } from '@amzn/awsui-design-tokens';
import React from 'react';
import { ToggleableDatetime } from './ToggleableDatetime';

// Manually defined regex to parse the logs into a slightly more readable, user-friendly format (feel free to add more)
// good examples in beta/gamma with a diverse group of logs
// https://beta.hammerstone.us-east-1.data-tools.aws.a2z.com/activity/monitor/49830/bdc12de3-928f-4c0c-a01b-3fe7dca9e3eb?groupName=AWS-DW-PRIMARY
// https://beta.hammerstone.us-east-1.data-tools.aws.a2z.com/activity/monitor/46216/73ee9b3a-5b39-4e7a-8b23-c024d99790b6?groupName=Hammerstone-TestGroup
const LOG_LINE_REGEX =
	/^(?<timestamp>.+ utc) (?<networkFile>\w+.\w+) (?<networkOrigin>.+) \[(?<logType>info|fatal|error|warn|debug)\] (?<fileOrigin>.+):(?<fileLine>\d+) (?<message>.*)/i;
const warningRegex = /^([^\:]+):(\d+): warning: (.+)/i;

// Colors set based on customer request: https://t.corp.amazon.com/V586796833/communication
// Following Polaris light mode convention as much as possible: https://polaris.a2z.com/foundation/visual-foundation/colors/
const originStyle: React.CSSProperties = { color: colorTextStatusInfo };
const typeStyle: React.CSSProperties = { textTransform: 'uppercase', fontWeight: 'bold' };

interface LogDisplayControls {
	rawToggle?: boolean;
	pathToggle?: boolean;
	timestampToggle?: boolean;
}
/**
 * @param {string[]} logsList A list of string logs to be displayed
 * @param {string} filterText A list of search term strings separated by spaces (' ') that determines what logs to show
 * @param {boolean} rawToggle Whether to display the logs as raw strings or to format them according to regex patterns
 * @param {boolean} pathToggle Whether to display the logs with the Apollo host, code file, and line number
 * @param {boolean} timestampToggle Whether to display the logs with timestamps, if they have any (e.g. warning does not)
 */
interface FormattedLogsProps extends LogDisplayControls {
	logsList: string[];
	filterText?: string;
}

/** A function which maps log type to the CSS styling of the beautified log line */
function toLogTypeStyle(logType: string): React.CSSProperties {
	switch (logType.toUpperCase()) {
		case 'WARN':
			return { ...typeStyle, color: '#232f3e', backgroundColor: '#ff9900' };
		case 'ERROR':
		case 'FATAL':
			return { ...typeStyle, color: '#232f3e', backgroundColor: '#ff5d64' };
		case 'DEBUG':
			return { ...typeStyle, color: '#fafafa', backgroundColor: '#545b64' };
		case 'INFO':
			return { ...typeStyle, color: '#232f3e', backgroundColor: '#99cbe4' };
		default:
			return typeStyle;
	}
}

/** A function which maps log type to the CSS styling of the text to highlight FATAL and ERROR logs */
function toTextStyle(logType: string): React.CSSProperties {
	switch (logType.toUpperCase()) {
		case 'ERROR':
		case 'FATAL':
			return { ...typeStyle, color: '#d13212' };
		default:
			return {};
	}
}

/** A function which maps log type to the icon props to display in the beautified logs */
function toIconProps(logType: string): IconProps {
	switch (logType.toUpperCase()) {
		case 'WARN':
			return { name: 'status-warning', variant: 'warning' };
		case 'ERROR':
		case 'FATAL':
			return { name: 'bug', variant: 'error' };
		case 'DEBUG':
			return { name: 'caret-right-filled', variant: 'error' };
		case 'INFO':
		default:
			return { name: 'status-info', variant: 'normal' };
	}
}

/**
 * @param props A group of configurable logs to display with certain settings
 * @returns A component listing out the logs in a readable format
 */
export default function FormattedLogs(props: FormattedLogsProps) {
	// parse log filter text, but remove empty strings as they are not used by user
	const searchTerms: string[] = (props.filterText ?? '').split(' ').filter((term) => term.trim());

	// compile regex once for efficient matching - matches any of the search terms (case-insensitive)
	const searchRegex = React.useMemo(() => {
		if (searchTerms.length === 0) return null;
		// escape special regex characters and join with OR operator
		const escapedTerms = searchTerms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
		return new RegExp(escapedTerms.join('|'), 'i');
	}, [searchTerms.join('|')]);

	// filter log lines using compiled regex or display all lines if no terms are searched
	const componentList = (props.logsList || [])
		.filter((line) => !searchRegex || searchRegex.test(line))
		.map((line) => <BeautifiedLogLine {...props} line={line} />);

	return <SpaceBetween size="m">{React.Children.toArray(componentList)}</SpaceBetween>;
}

/**
 * @param props A group of configurable logs to display with certain settings
 * @param line Individual log line to augment
 * @returns Beautiful JSX of one log line
 */
function BeautifiedLogLine(props: LogDisplayControls & { line: string }) {
	const { line } = props;
	const match = line.match(LOG_LINE_REGEX);
	const warningMatch = line.match(warningRegex);

	if (props.rawToggle) {
		//If raw return a basic component containing the text
		return <code style={{ fontSize: '0.9em' }}>{line}</code>;
	}

	// Decorate and beautify the logs based on log type
	if (match) {
		const { timestamp, networkOrigin, logType, fileOrigin, fileLine, message } = match.groups ?? {};

		return (
			<Box variant="code">
				{props.timestampToggle && <ToggleableDatetime noCopy>{timestamp}</ToggleableDatetime>}
				{props.pathToggle && (
					<>
						<div>
							Running on host <span style={originStyle}>{networkOrigin}</span>
						</div>
						<div>
							From <span style={originStyle}>{fileOrigin}</span> at line {fileLine}:
						</div>
					</>
				)}
				<div>
					<span style={toLogTypeStyle(logType)}>
						<Icon {...toIconProps(logType)} size="small" />
						{logType.toUpperCase()}
					</span>
					<span style={toTextStyle(logType)}>: {message}</span>
				</div>
			</Box>
		);
	} else if (warningMatch) {
		// Make sure to update these named indexes if the Regex Expression above changes!
		const [original, fileOrigin, fileLine, message] = warningMatch;

		[original]; // Do nothing with these variables
		return (
			<Box variant="code">
				{props.pathToggle && (
					<>
						<div>
							From <span style={originStyle}>{fileOrigin}</span> at line {fileLine}:
						</div>
					</>
				)}
				<div>
					<span style={toLogTypeStyle('WARN')}>
						<Icon {...toIconProps('WARN')} size="small" /> WARN
					</span>
					<span> {message}</span>
				</div>
			</Box>
		);
	} else {
		return <Box variant="code">{line}</Box>;
	}
}
